import { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  FieldError,
  FieldErrorsImpl,
  Merge,
  Path,
  UseFormRegister,
} from 'react-hook-form';

interface SelectImageInputProps<
  T extends Record<string, unknown>,
> extends React.InputHTMLAttributes<HTMLInputElement> {
  allowSelectMultiple: boolean;
  name: Path<T>;
  register?: UseFormRegister<T>;
  error?: Merge<
    FieldError,
    (
      | Merge<
          FieldError,
          FieldErrorsImpl<{
            name: NonNullable<string>;
            size: NonNullable<number>;
            type: NonNullable<string>;
            data: NonNullable<string>;
          }>
        >
      | undefined
    )[]
  >;
  onSelectedImages?: (images: File[]) => void;
}

export interface SelectImageInputRef {
  onClickBtn: () => void;
}

const SelectImageInput = forwardRef<
  SelectImageInputRef,
  SelectImageInputProps<any>
>(
  (
    {
      allowSelectMultiple = false,
      name,
      register,
      error,
      onSelectedImages,
      ...props
    }: SelectImageInputProps<any>,
    ref,
  ) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(ref, () => ({
      onClickBtn: () => fileInputRef.current && fileInputRef.current.click(),
    }));

    return (
      <input
        type="file"
        ref={(e) => {
          if (e) {
            register?.(name).ref(e);
            fileInputRef.current = e;
          }
        }}
        multiple={allowSelectMultiple}
        accept="image/*"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          event.preventDefault();
          if (event.target.files) {
            const files: File[] = Array.from(event.target.files);
            onSelectedImages?.(files);
          } else {
            onSelectedImages?.([]);
          }
        }}
        {...props}
      />
    );
  },
);

export default SelectImageInput;
